import { DraftType, type Draft } from "../../../api/types/draft";
import { computeExhibitLabels } from "../../caseFiles/exhibitLabels";
import { nodeTitle } from "../../caseFiles/nodeNames";
import type { ExhibitScheme, FsNode } from "../../caseFiles/types";
import type { CasePrepUscisFilledForm } from "../../casePrepUscisForms/types";
import {
  draftDetail,
  draftTitle,
  formDetail,
  formName,
  isResearchMemo
} from "../workspace/workspaceModel";

/** A work product that can be added to Case Files. */
export type CopySource = {
  kind: "draft" | "form";
  id: string;
  title: string;
  /** The secondary line: "Draft · Letter", "Form · Edition 01/20/25". */
  detail?: string | null;
  /** What the draft is, for the suggested folder. */
  role?: "letter" | "exhibit_list" | "research" | "form";
};

export const sourceKey = (kind: string, id: string): string => `${kind}:${id}`;

export const draftCopySource = (draft: Draft): CopySource => {
  const detail = draftDetail(draft);
  return {
    kind: "draft",
    id: draft.draft_id,
    title: draftTitle(draft),
    detail: detail ? `Draft · ${detail}` : "Draft",
    role:
      draft.draft_type === DraftType.EXHIBIT_LIST
        ? "exhibit_list"
        : isResearchMemo(draft)
        ? "research"
        : "letter"
  };
};

export const formCopySource = (form: CasePrepUscisFilledForm): CopySource => ({
  kind: "form",
  id: form.case_extend_uscis_filled_form_id,
  title: formName(form),
  detail: `Form · ${formDetail(form)}`,
  role: "form"
});

/** `kind:id` -> the live copy of that work product in Case Files (the first, in tree order). */
export const copiesBySource = (nodes: FsNode[]): Map<string, FsNode> => {
  const out = new Map<string, FsNode>();
  nodes.forEach((node) => {
    const ref = node.source_ref;
    if (node.is_deleted || !ref?.kind || !ref.id) return;
    const key = sourceKey(ref.kind, ref.id);
    if (!out.has(key)) out.set(key, node);
  });
  return out;
};

const ROLE_FOLDERS: Record<NonNullable<CopySource["role"]>, RegExp> = {
  form: /\b(forms?|uscis|notices?|petition)\b/i,
  exhibit_list: /\b(index|exhibit list|cover)\b/i,
  research: /\b(research|memos?|legal)\b/i,
  letter:
    /\b(letters?|support|employ\w*|declarations?|affidavits?|testimon\w*)\b/i
};

/** A folder the copy plausibly belongs in, by what it is; null when nothing fits. */
export const suggestFolder = (
  folders: FsNode[],
  source: Pick<CopySource, "kind" | "role">
): string | null => {
  const role = source.role ?? (source.kind === "form" ? "form" : "letter");
  const match = folders.find(
    (f) => !f.is_deleted && ROLE_FOLDERS[role].test(nodeTitle(f))
  );
  return match?.id ?? null;
};

const PREVIEW_ID = "__add-to-case-files-preview__";

/**
 * The label a file appended to `parentId` would take, and the labeled file it
 * would follow ("B.4 Org Chart"). Null label when it would not be numbered.
 */
export const previewLabel = (
  nodes: FsNode[],
  scheme: ExhibitScheme,
  parentId: string | null,
  include: boolean
): { label: string | null; after: string | null } => {
  const live = nodes.filter((n) => !n.is_deleted);
  const siblings = live.filter((n) => (n.parent_id ?? null) === parentId);
  const lastKey = siblings.reduce(
    (max, n) => (n.sort_key > max ? n.sort_key : max),
    ""
  );
  const phantom: FsNode = {
    id: PREVIEW_ID,
    parent_id: parentId,
    kind: "file",
    name: "",
    sort_key: `${lastKey}~`,
    path_cache: "",
    packet_inclusion: include ? "included" : "excluded",
    exhibit_numbering: "numbered"
  };
  const labels = computeExhibitLabels([...live, phantom], scheme);
  const previous = [...siblings]
    .sort((a, b) => (a.sort_key < b.sort_key ? 1 : -1))
    .find((n) => labels[n.id]);
  return {
    label: labels[PREVIEW_ID] ?? null,
    after: previous ? `${labels[previous.id]} ${nodeTitle(previous)}` : null
  };
};
